import {dispatchEvent} from "./lib/events";

/**
 * Tries to resolve the field which should determine visibility states.
 * @param {Object} data The definition of the form.
 * @param {HTMLElement} $container The container in which to search for the field.
 *
 * @return null|{HTMLElement} The field element, or null if not found.
 */
function resolveManagerField(data, $container) {
    return $container.querySelector(`[name*="[attendees][${data.attendee_number}][meta][registration-type]"]`);
}

/**
 * Get the field containers which should be visible and hidden given an input managerValue.
 *
 * @param {HTMLElement} $manager The manager field, value of which is used to determine which fields to locate.
 * @param {Object} data The form definition.
 * @param {HTMLElement} $container The contain in which to locate the fields.
 * @param {boolean} setFieldToDefault If true, will set the value of the field to the default value (usually empty).
 * @return {{$visible: HTMLElement[], $hidden: HTMLElement[]}} The field containers which should be visible and which should be hidden.
 */
function resolveManagedFieldContainers($manager, data, $container, setFieldToDefault = true) {
    const managerValue = $manager.value;

    const visibleSlugs = data?.visibility_rules[managerValue] || [];

    // Combine all possible values under visibility rules for the hidden slugs outside the visible group.
    const hiddenSlugs = Object.values(data?.visibility_rules || {})
        .reduce((accumulator, slugs) => {
            return [...accumulator, ...slugs];
        })
        .filter(slug => !visibleSlugs.includes(slug));

    const resolveFieldWithSlug = (slug) => {
        const $element = $container.querySelector(`[name*="[attendees][${data.attendee_number}][meta][${slug}]"]`);
        if ($element) {
            $element.dataset.agendAttendeeSlug = slug;
        }
        return $element;
    }

    const resolveFieldContainer = ($element) => {
        return $element ? $element.closest('.tribe-tickets__form-field') : null;
    }

    const isViableElement = ($element) => {
        return !!$element && $element !== $manager;
    }

    // Discover all fields which should be visible, and set default values.
    const $visible = visibleSlugs
        .map(resolveFieldWithSlug)
        .filter(isViableElement)
        .map($element => {
            if (setFieldToDefault) {
                $element.value = data?.default_values?.[managerValue]?.[$element.dataset.agendAttendeeSlug] || '';
            }
            return $element;
        })
        .map(resolveFieldContainer)
        .filter(isViableElement);

    // Discover all fields which should not be visible, and empty the managerValue.
    const $hidden = hiddenSlugs
        .map(resolveFieldWithSlug)
        .filter(isViableElement)
        .map($element => {
            if (setFieldToDefault) {
                $element.value = '';
                dispatchEvent($element, 'agend.attendee-form-visibility.hidden-field-cleared', {$manager, $element});
            }
            return $element;
        })
        .map(resolveFieldContainer)
        .filter(isViableElement);

    return {$visible, $hidden};
}

/**
 * Iterates all known forms with managed visibility and updates the available registration types, based on the incoming
 * fields value.
 * @param {Object} data
 * @param {HTMLSelectElement|HTMLElement} $sourceField
 */
function updateAllowedRegistrationTypes(data, $sourceField) {

    const previousValue = data._currentRegistrationTypeValue || null;
    const value = $sourceField.value;
    const typesAllowingMultiple = data?.types_allowing_multiple_registrations || [];

    const didAllowMultiple = typesAllowingMultiple.indexOf(previousValue) !== -1;
    const nowAllowsMultiple = typesAllowingMultiple.indexOf(value) !== -1;

    // Determine a list of viable managers.
    const $managers = agendAttendeeManagedVisibilityForms
        .filter(formData => formData.form_selector === data.form_selector)
        .map(formData => formData._$managerField)
        .filter($manager => $manager && $manager !== $sourceField);

    // Enable all corresponding values in visibility managers if this was a previously restricted value...
    if (!didAllowMultiple && previousValue !== null) {
        $managers.forEach($manager => {
            $manager.querySelectorAll(`option[value="${previousValue}"]`).forEach($option => {
                $option.disabled = false;
            });
        });
    }

    // Do nothing, the specified type is allowed multiple times.
    if (nowAllowsMultiple) {
        return;
    }

    // ...then disable the other manager field options so it cannot be selected there based on the new value.
    $managers.forEach($manager => {
        $manager.querySelectorAll(`option[value="${value}"]`).forEach($option => {
            $option.disabled = true;
        });
    });
}

/**
 * Prepares an attendee form for the visibility behaviour.
 * @param {Object} data
 */
function prepareFormVisibilityBehaviour(data) {
    if (!data.form_selector) {
        console.warn('Form data provided with no form lookup field.', data);
        return;
    }

    const $form = document.querySelector(data.form_selector);

    // Build a key to check for duplicate bindings. By using an instance ID like this, we can
    // create multiple instances of lookups on the same form without conflicting with each other.
    // The best example of this is the Tribe Attendee form, where it is a single form containing
    // multiple sets of Attendee fields.
    const datasetKey = `attendee-managed-visibility-form-${data.instance_id}`;
    if ($form.dataset[datasetKey]) {
        console.info('Looks like this container has already been setup for managed visibility.', data);
        return;
    }

    const $managerField = resolveManagerField(data, $form);

    if (!$managerField) {
        console.warn(`Could not locate the visibility manager field for attendee ${data.attendee_number}.`);
        return;
    }

    // Track whether we've dispatched the first change or not. This is so we can safely init the form without wiping
    // any existing values which may have been set via other behaviour (such as attendee form population).
    data._dispatchedFirstChange = false;

    // Setup classes on fields.
    const fields = resolveManagedFieldContainers($managerField, data, $form, data._dispatchedFirstChange);
    fields.$hidden.forEach($element => $element.classList.add('agend-attendee-managed-visibility'));
    fields.$visible.forEach($element => $element.classList.add('agend-attendee-managed-visibility'));

    $managerField.addEventListener('change', event => {
        const fields = resolveManagedFieldContainers($managerField, data, $form, data._dispatchedFirstChange);

        // Hide the fields which should be hidden.
        fields.$hidden.forEach($element => $element.classList.add('agend-attendee-managed-visibility--hidden'));

        // Remove the display property from the fields which should be visible, resetting to whatever their original
        // display should be, supporting flex/block/etc.
        fields.$visible.forEach($element => $element.classList.remove('agend-attendee-managed-visibility--hidden'));

        updateAllowedRegistrationTypes(data, $managerField);

        data._currentRegistrationTypeValue = $managerField.value;
        dispatchEvent(window, 'agend.attendee-form-visibility.manager-field-changed', {$managerField, data, $form, fields});
    });

    // Pack out the data object with useful references.
    data._$form = $form;
    data._$managerField = $managerField;
    data._$fields = fields;
    data._currentRegistrationTypeValue = $managerField.value;
}

/**
 * Defines and/or modifies the push method on the collection of form data, so
 * that behaviour can apply to any async loaded forms with our JS snippet.
 */
function prepareFormsList() {
    window.agendAttendeeManagedVisibilityForms = window.agendAttendeeManagedVisibilityForms || [];

    const originalPushMethod = window.agendAttendeeManagedVisibilityForms.push;

    window.agendAttendeeManagedVisibilityForms.push = function (...newItems) {
        // Init the forms...
        newItems.forEach(formData => prepareFormVisibilityBehaviour(formData));
        // ...and then append to array as per usual.
        originalPushMethod.call(window.agendAttendeeManagedVisibilityForms, ...newItems);

        // Trigger the change event on the manager field to ensure the form is initialized. We do this outside
        // the actual setup function, as there may be multiple forms which need to be accounted for.
        newItems.forEach(formData => {
            if (formData._$managerField) {
                formData._$managerField.dispatchEvent(new Event('change'));
            }
        });
    }
}

function initializeAllForms() {
    if (window.agendAttendeeManagedVisibilityForms.length === 0) {
        return;
    }
    window.agendAttendeeManagedVisibilityForms.forEach(formData => prepareFormVisibilityBehaviour(formData));

    // Trigger the change event on the manager field to ensure any restrictions are applied. We do this outside
    // the actual setup function, as there may be multiple forms which need to be accounted for after all have been
    // set up.
    window.agendAttendeeManagedVisibilityForms.forEach(formData => {
        if (formData._$managerField) {
            formData._$managerField.dispatchEvent(new Event('change'));
            // We're fully setup, use the normal behaviour on update from here on.
            formData._dispatchedFirstChange = true;
        }
    });
    dispatchEvent(window, 'agend.attendee-form-visibility.initialised', {});
}

document.addEventListener('DOMContentLoaded', event => {
    // Prepare to catch future forms.
    prepareFormsList();
    initializeAllForms();
});

window.addEventListener('tribe_et_after_form_prefills', event => {
    // Attempt again to initialise forms. Tribe may not have been ready when we did it with document ready.
    initializeAllForms();
});

